import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type OnboardingStep =
    | 'age'
    | 'socialproof'
    | 'walkingexperience'
    | 'walkingbenefits'
    | 'goalselection'
    | 'solutionoverview'
    | 'additionalgoals'
    | 'bodybuild'
    | 'dreambody'
    | 'targetzones'
    | 'weightchange'
    | 'bestshape'
    | 'stepbystep'
    | 'exercisefrequency'
    | 'walkfrequency'
    | 'breathstairs'
    | 'struggles'
    | 'walkingconcern'
    | 'achievegoal'
    | 'healthychoices'
    | 'losemotivation'
    | 'needreminders'
    | 'workschedule'
    | 'typicalday'
    | 'energylevels'
    | 'stresslevel'
    | 'sleepduration'
    | 'sleepissues'
    | 'sleepbenefits'
    | 'waterintake'
    | 'eatinghabits'
    | 'foodcravings'
    | 'dietpreference'
    | 'mealplanbenefits'
    | 'fitnessmotivation'
    | 'weightgainfactors'
    | 'height'
    | 'weight'
    | 'goalweight'
    | 'ageinput'
    | 'wellnessprofile'
    | 'importantevent'
    | 'eventdate'
    | 'goalprediction'
    | 'plangenerating'
    | 'email'
    | 'loading'
    | 'name'
    | 'planready'
    | 'discount'
    | 'paywall'
    | 'payment'
    | '1'
    | '2-1'
    | '2-2'
    | '3';

export interface FunnelData {
    ageGroup?: string;
    hasWalkingExperience?: boolean;
    goal?: string;
    additionalGoals?: string[];
    bodyBuild?: string;
    dreamBody?: string;
    targetZone?: string;
    weightChange?: string;
    bestShape?: string;
    exerciseFrequency?: string;
    walkFrequency?: string;
    breathStairs?: string;
    struggles?: string[];
    walkingConcern?: string;
    healthyChoicesRating?: number;
    loseMotivationRating?: number;
    needRemindersRating?: number;
    workSchedule?: string;
    typicalDay?: string;
    energyLevels?: string;
    stressLevel?: string;
    sleepDuration?: string;
    sleepIssues?: string[];
    waterIntake?: string;
    eatingHabits?: string[];
    foodCravings?: string[];
    dietPreference?: string;
    fitnessMotivation?: string;
    weightGainFactors?: string[];
    height?: number;
    heightUnit?: 'cm' | 'ft';
    weight?: number;
    weightUnit?: 'kg' | 'lbs';
    goalWeight?: number;
    goalWeightUnit?: 'kg' | 'lbs';
    age?: number;
    importantEvent?: string;
    eventDate?: string;
    [key: string]: string | boolean | string[] | number | undefined;
}

export type PlanType =
    | 'monthly'
    | 'yearly'
    | '1_week_trial'
    | '4_week'
    | '12_week';

export interface UserData {
    fullName: string;
    email: string;
    password: string;
    selectedOffer: string | null;
    selectedPlan: PlanType | null;
    selectedPlanId: string | null;
    promoCode: string;

    // Add fields for Meta CAPI tracking
    firstName?: string;
    lastName?: string;
    external_id?: string;

    // Funnel data
    funnelData?: FunnelData;
}

interface OnboardingState {
    // State
    currentStep: OnboardingStep;
    userData: UserData;
    backFromPayment: boolean;

    // Actions
    setCurrentStep: (step: OnboardingStep) => void;
    setUserData: (data: Partial<UserData>) => void;
    setEmail: (email: string) => void;
    setPlan: (planType: PlanType, planId: string | null) => void; // UPDATED
    setAccountInfo: (fullName: string, email: string, password: string) => void;
    setOffer: (offer: string | null) => void;
    setBackFromPayment: (value: boolean) => void;
    resetOnboarding: () => void;
    // Add actions for Meta CAPI fields
    setExternalId: (externalId: string) => void;
    getSplitName: () => { firstName: string, lastName: string };
    getSafeUserData: () => UserData;
    getTrackingUserData: () => { em: string; fn: string; ln: string; external_id: string };
    // Funnel data
    setFunnelData: (data: Partial<FunnelData>) => void;
}

const initialUserData: UserData = {
    fullName: '',
    email: '',
    password: '',
    selectedOffer: null,
    selectedPlan: null,
    selectedPlanId: null,
    promoCode: '',
    firstName: '',
    lastName: '',
    external_id: '',
    funnelData: {}
};

export const useOnboardingStore = create<OnboardingState>()(
    persist(
        (set, get) => ({
            currentStep: 'age',
            userData: initialUserData,
            backFromPayment: false,

            setCurrentStep: (step) => set({ currentStep: step }),

            setUserData: (data) =>
                set((state) => ({
                    userData: { ...state.userData, ...data }
                })),

            setEmail: (email) =>
                set((state) => ({
                    userData: { ...state.userData, email }
                })),

            setPlan: (planType, planId) =>
                set((state) => ({
                    userData: { ...state.userData, selectedPlan: planType, selectedPlanId: planId }
                })),

            setAccountInfo: (fullName, email, password) => {
                const names = fullName.split(' ');
                const firstName = names[0] || '';
                const lastName = names.slice(1).join(' ') || '';

                set((state) => ({
                    userData: {
                        ...state.userData,
                        fullName,
                        email,
                        password,
                        firstName,
                        lastName
                    }
                }));
            },

            setOffer: (offer) =>
                set((state) => ({
                    userData: { ...state.userData, selectedOffer: offer }
                })),

            setBackFromPayment: (value) => set({ backFromPayment: value }),

            resetOnboarding: () =>
                set({
                    currentStep: 'age',
                    userData: initialUserData
                }),

            setFunnelData: (data) =>
                set((state) => ({
                    userData: {
                        ...state.userData,
                        funnelData: { ...state.userData.funnelData, ...data }
                    }
                })),

            setExternalId: (externalId) =>
                set((state) => ({
                    userData: { ...state.userData, external_id: externalId }
                })),

            getSplitName: () => {
                const { fullName } = get().userData;
                const names = fullName.split(' ');
                return {
                    firstName: names[0] || '',
                    lastName: names.slice(1).join(' ') || ''
                };
            },

            getSafeUserData: () => {
                const userData = get().userData;
                return {
                    ...userData,
                    password: '' // Never expose password
                };
            },

            getTrackingUserData: () => {
                const { email, firstName, lastName, external_id } = get().userData;
                return {
                    em: email,
                    fn: firstName || '',
                    ln: lastName || '',
                    external_id: external_id || ''
                };
            }
        }),
        {
            name: 'onboarding-storage',
            storage: createJSONStorage(() => localStorage)
        }
    )
);